import { useEffect, useState } from "react";
import { brandName } from "@/lib/brand";
import { toast } from "sonner";
import { ExternalLink, Globe, Users, Briefcase, Save, Loader2, FileSpreadsheet, Copy, Pencil } from "lucide-react";
import { updatePublishing } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const DESTS = [
  ["facebook_group", "Facebook Group", Users],
  ["facebook_page", "Facebook Business Page", Briefcase],
  ["website", "Website", Globe],
];
const STATUS = {
  not_posted: ["Not Posted", "bg-white text-stone-500 border-stone-300"],
  pending: ["Pending", "bg-amber-50 text-amber-900 border-amber-300"],
  published: ["Published", "bg-emerald-50 text-emerald-800 border-emerald-300"],
  failed: ["Failed", "bg-red-50 text-red-700 border-red-300"],
};

import { fmtMT } from "@/lib/mountainTime";
const fmtDT = (v) => fmtMT(v) || null;
const toLocalInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

function DestinationCard({ horseId, dest, label, Icon, data, onChange }) {
  const [url, setUrl] = useState(data.url_source === "smartsheet" ? "" : data.url || "");
  const [when, setWhen] = useState(toLocalInput(data.published_at));
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [label_, tone] = STATUS[data.status] || STATUS.not_posted;

  const save = async (changes) => {
    setBusy(true);
    try {
      onChange(await updatePublishing(horseId, dest, changes));
      toast.success(`${label} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`publish-card-${dest}`} className="bg-white border border-stone-200 rounded-xl p-4 sm:p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="h-5 w-5 text-amber-800 shrink-0" />
          <h3 className="font-semibold text-stone-900 truncate">{label}</h3>
        </div>
        <span data-testid={`publish-status-${dest}`} className={`shrink-0 text-xs font-semibold border rounded-full px-2.5 py-0.5 ${tone}`}>{label_}</span>
      </div>

      <div className="mt-3">
        <label className="text-[11px] uppercase tracking-wider font-semibold text-stone-400">Published URL</label>
        <Input
          data-testid={`publish-url-${dest}`}
          readOnly
          value={data.url || ""}
          placeholder="No link yet"
          onFocus={(e) => e.target.select()}
          className="mt-1 h-11 sm:h-10 w-full min-w-0 text-base sm:text-sm font-mono bg-stone-50 border-stone-200 text-stone-800 truncate"
        />
        {data.url_source === "smartsheet" && (
          <div data-testid={`publish-source-${dest}`} className="mt-1 flex items-center gap-1 text-xs text-emerald-700">
            <FileSpreadsheet className="h-3.5 w-3.5" /> Imported from Smartsheets ({data.smartsheet_column} column)
          </div>
        )}
        {data.url_source && data.url_source !== "smartsheet" && (
          <div data-testid={`publish-source-${dest}`} className="mt-1 text-xs text-amber-800">Entered {data.url_source === "manual" ? "manually" : `by ${data.url_source}`}</div>
        )}
        <div className="mt-2 grid grid-cols-2 sm:flex gap-2">
          <Button data-testid={`publish-copy-${dest}`} disabled={!data.url} onClick={() => copyText(data.url, `${label} link copied`)}
            className="h-11 sm:h-10 rounded-full bg-amber-800 hover:bg-amber-900 text-white">
            <Copy className="h-4 w-4 mr-2" />Copy Link
          </Button>
          <Button data-testid={`publish-open-${dest}`} variant="outline" disabled={!data.url} asChild={!!data.url}
            className="h-11 sm:h-10 rounded-full border-amber-800 text-amber-800">
            {data.url ? (
              <a href={data.url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4 mr-2" />Open Link</a>
            ) : (
              <span><ExternalLink className="h-4 w-4 mr-2" />Open Link</span>
            )}
          </Button>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-500">
          <span data-testid={`publish-date-${dest}`}>Published: {fmtDT(data.published_at) || (data.status_source === "smartsheet" ? "date not recorded in Smartsheets" : "—")}</span>
          {data.external_post_id && <span>Post ID: {data.external_post_id}</span>}
          {data.error && <span className="text-red-600">Error: {data.error}</span>}
        </div>
      </div>

      <button data-testid={`publish-edit-toggle-${dest}`} onClick={() => setEditing((v) => !v)}
        className="mt-3 inline-flex items-center gap-1.5 min-h-[44px] sm:min-h-0 text-xs font-semibold text-stone-500 hover:text-amber-800">
        <Pencil className="h-3.5 w-3.5" /> {editing ? "Hide edit options" : "Edit status or link"}
      </button>

      {editing && (<>
      {dest === "website" ? (
        <p data-testid="publish-website-status-note" className="mt-2 text-xs text-stone-500">Status follows the ad's website state (Publish / Unpublish / Hide).</p>
      ) : <div className="mt-2">
        <Select value={data.status} onValueChange={(v) => save({ status: v })} disabled={busy}>
          <SelectTrigger data-testid={`publish-status-select-${dest}`} className="h-11 sm:h-10 border-stone-200">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>}

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_200px_auto] gap-2">
        <Input data-testid={`publish-url-input-${dest}`} value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder={data.smartsheet_url ? "Override Smartsheet link (optional)" : "https://…"}
          className="h-11 sm:h-10 text-base sm:text-sm border-stone-200" />
        <Input data-testid={`publish-date-input-${dest}`} type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
          className="h-11 sm:h-10 text-base sm:text-sm border-stone-200" />
        <Button data-testid={`publish-save-${dest}`} disabled={busy}
          onClick={() => save({ url: url.trim(), published_at: when ? new Date(when).toISOString() : null })}
          className="h-11 sm:h-10 rounded-full bg-amber-800 hover:bg-amber-900 text-white">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" />Save</>}
        </Button>
      </div>
      {data.smartsheet_url && data.url_source !== "smartsheet" && (
        <p className="mt-2 text-[11px] text-stone-400 break-all">Smartsheet link kept: {data.smartsheet_url}</p>
      )}
      </>)}
    </div>
  );
}

export function PublishingTab({ horse, onChange, focusSection }) {
  const pd = horse.publishing_destinations;
  useEffect(() => {
    if (!focusSection || !pd) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="publish-card-${focusSection}"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-[#1877F2]"); }
    }, 250);
    return () => clearTimeout(t);
  }, [focusSection, !!pd]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!pd) return null;
  return (
    <div data-testid="publishing-tab" className="space-y-4">
      <p className="text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
        Each destination is tracked separately. Facebook Group links imported from Smartsheets count as Published unless you set a different status. Smartsheet links are never overwritten.
      </p>
      {DESTS.map(([dest, l, Icon]) => [dest, dest === "website" ? `${brandName()} Website`.trim() : l, Icon]).map(([dest, label, Icon]) => (
        <DestinationCard key={`${dest}-${pd[dest].updated_at || ""}`} horseId={horse.id} dest={dest} label={label} Icon={Icon} data={pd[dest]} onChange={onChange} />
      ))}
    </div>
  );
}
